import React, { useMemo, useEffect, useState } from 'react';
import { useLMS } from '../../context/LMSContext';

const StudentDashboard = () => {
  const {
    currentUser,
    currentCohort,
    getVisibleModulesForCohort,
    currentCohortEvents,
    getModuleProgress,
    isItemComplete,
    setCurrentView,
    setSelectedWeek,
    getSubmissionsWithFeedback,
    getSubmissionForItem,
    modules: allModules,
    unreadMessageCount
  } = useLMS();

  const cohort = currentCohort;
  // Students must only see modules that are visible for their cohort.
  // Using the global module list here leaks locked weeks on mobile where the
  // sidebar (which already filters) is hidden behind a hamburger toggle.
  const modules = cohort?.id ? (getVisibleModulesForCohort(cohort.id) || []) : [];
  const events = currentCohortEvents || [];

  // For live countdown timer
  const [now, setNow] = useState(new Date());

  // Update "now" every minute for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const nextWeek = useMemo(() => {
    const d = new Date(today);
    d.setDate(today.getDate() + 7);
    return d;
  }, [today]);

  // Timezone-safe date parsing helper
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Parse date with time
  const parseDateTime = (dateString, timeString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    if (timeString) {
      const [hours, minutes] = timeString.split(':').map(Number);
      date.setHours(hours, minutes, 0, 0);
    }
    
    return date;
  };

  // =========================================================================
  // CURRENT WEEK CALCULATION
  // =========================================================================
  const currentWeek = useMemo(() => {
    const startDateStr = cohort?.startDate || cohort?.start_date;
    if (!startDateStr) return 1;
    
    const start = parseLocalDate(startDateStr);
    if (!start) return 1;
    
    start.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today - start) / (1000 * 60 * 60 * 24));
    const week = Math.ceil((diffDays + 1) / 7);
    return Math.min(6, Math.max(1, week));
  }, [cohort?.startDate, cohort?.start_date, today]);

  // =========================================================================
  // UPCOMING EVENTS (filtered and sorted)
  // =========================================================================
  const upcomingEvents = useMemo(() => {
    return events
      .map(e => ({
        ...e,
        _date: e.event_date || e.date,
        _startTime: e.start_time || e.startTime,
        _endTime: e.end_time || e.endTime
      }))
      .filter(e => {
        if (!e._date) return false;
        const eventDate = parseLocalDate(e._date);
        if (!eventDate) return false;
        return eventDate >= today && eventDate <= nextWeek;
      })
      .sort((a, b) => {
        const dateA = parseLocalDate(a._date);
        const dateB = parseLocalDate(b._date);
        return (dateA || 0) - (dateB || 0);
      })
      .slice(0, 6);
  }, [events, today, nextWeek]);

  // =========================================================================
  // FEATURE #6: NEXT DUE ITEM
  // Find the next upcoming assignment/quiz/exam that's due
  // =========================================================================
  const nextDueItem = useMemo(() => {
    const now = new Date();
    
    return events
      .filter(e => {
        const type = e.type || e.itemType;
        return ['assignment', 'quiz', 'exam', 'workbook'].includes(type);
      })
      .map(e => ({
        ...e,
        _date: e.event_date || e.date,
        _dateObj: parseLocalDate(e.event_date || e.date)
      }))
      .filter(e => e._dateObj && e._dateObj >= today)
      .sort((a, b) => a._dateObj - b._dateObj)
      [0] || null;
  }, [events, today]);

  // =========================================================================
  // FEATURE #7: LIVE LECTURE COUNTDOWN
  // Find if there's a lecture happening today and calculate countdown
  // =========================================================================
  const liveLectureInfo = useMemo(() => {
    const todayStr = today.toISOString().split('T')[0];
    
    const todaysLectures = events
      .filter(e => {
        const type = e.type || e.itemType;
        const eventDate = e.event_date || e.date;
        return (type === 'lecture' || type === 'live-lecture') && eventDate === todayStr;
      })
      .map(e => ({
        ...e,
        _startTime: e.start_time || e.startTime || '11:30',
        _endTime: e.end_time || e.endTime || '15:00'
      }));

    if (todaysLectures.length === 0) return null;

    const lecture = todaysLectures[0];
    const lectureStart = parseDateTime(todayStr, lecture._startTime);
    const lectureEnd = parseDateTime(todayStr, lecture._endTime);

    if (!lectureStart) return null;

    const diffMs = lectureStart - now;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;

    // Lecture is in the past for today
    if (lectureEnd && now > lectureEnd) {
      return null;
    }

    // Lecture is currently happening
    if (now >= lectureStart && lectureEnd && now <= lectureEnd) {
      return {
        ...lecture,
        status: 'live',
        message: 'Live now!'
      };
    }

    // Lecture is coming up
    if (diffMins > 0) {
      let countdown = '';
      if (diffHours > 0) {
        countdown = `${diffHours}h ${remainingMins}m`;
      } else {
        countdown = `${diffMins} min`;
      }

      return {
        ...lecture,
        status: 'upcoming',
        countdown,
        message: `Starts in ${countdown}`
      };
    }

    return null;
  }, [events, today, now]);

  // =========================================================================
  // FEATURE #5: WEEKLY PROGRESS
  // Calculate items completed vs total for the current week
  // =========================================================================
  const weeklyProgress = useMemo(() => {
    const currentModule = modules.find(m => 
      (m.weekNumber || m.week_number) === currentWeek
    );
    
    if (!currentModule) {
      return { completed: 0, total: 0, percentage: 0 };
    }

    const items = currentModule.items || currentModule.module_items || [];
    const total = items.length;
    const completed = items.filter(item => isItemComplete(item.id)).length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  }, [modules, currentWeek, isItemComplete]);

  // =========================================================================
  // FEATURE #1: MODULE PROGRESS (using context function)
  // =========================================================================
  const getModuleProgressData = (weekNumber) => {
    return getModuleProgress(weekNumber);
  };

  // =========================================================================
  // FEATURE: WEEKLY ASSIGNMENT COMPLETION
  // For each module, count submittable items (assignment, quiz, exam, workbook)
  // and check how many have been submitted
  // =========================================================================
  const SUBMITTABLE_TYPES = ['assignment', 'quiz', 'exam', 'workbook'];

  const getAssignmentStatus = (module) => {
    const items = module.items || module.module_items || [];
    const submittableItems = items.filter(item => SUBMITTABLE_TYPES.includes(item.type));
    const total = submittableItems.length;
    if (total === 0) return { submitted: 0, total: 0, allDone: true, hasAssignments: false };
    
    const submitted = submittableItems.filter(item => {
      const submission = getSubmissionForItem ? getSubmissionForItem(item.id) : null;
      return submission != null;
    }).length;

    return { submitted, total, allDone: submitted >= total, hasAssignments: true };
  };

  // =========================================================================
  // FORMATTING HELPERS
  // =========================================================================
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getEventTypeIcon = (type) => {
    // Return Zoom logo for live-lecture type
    if (type === 'live-lecture' || type === 'lecture') {
      return (
        <img 
          src="https://cdn.shopify.com/s/files/1/0659/6208/2392/files/zoom_logo_icon_147196.png?v=1769335319" 
          alt="Zoom" 
          style={{ width: '20px', height: '20px', objectFit: 'contain' }}
        />
      );
    }
    
    const icons = {
      assignment: '📝',
      quiz: '❓',
      exam: '📋',
      workbook: '📒',
      other: '📅'
    };
    return icons[type] || '📅';
  };

  const getDueUrgencyClass = (dateString) => {
    if (!dateString) return '';
    const dueDate = parseLocalDate(dateString);
    if (!dueDate) return '';
    
    const diffDays = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'overdue';
    if (diffDays === 0) return 'due-today';
    if (diffDays === 1) return 'due-tomorrow';
    if (diffDays <= 3) return 'due-soon';
    return '';
  };

  const getDueLabel = (dateString) => {
    if (!dateString) return null;
    const dueDate = parseLocalDate(dateString);
    if (!dueDate) return null;
    
    const diffDays = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due Today';
    if (diffDays === 1) return 'Due Tomorrow';
    if (diffDays <= 3) return `Due in ${diffDays} days`;
    return null;
  };

  // =========================================================================
  // NAVIGATION
  // =========================================================================
  const handleModuleClick = (weekNumber) => {
    setSelectedWeek(weekNumber);
    setCurrentView('module');
  };

  // =========================================================================
  // RENDER: No cohort state
  // =========================================================================
  if (!cohort) {
    return (
      <div className="student-dashboard">
        <div className="page-header">
          <h1>Welcome, {currentUser?.name?.split(' ')[0] || 'Student'}! 👋</h1>
          <p>You are not currently enrolled in a cohort. Please contact support.</p>
        </div>
      </div>
    );
  }

  const cohortStartDate = cohort.startDate || cohort.start_date;
  const cohortEndDate = cohort.endDate || cohort.end_date;

  return (
    <div className="student-dashboard">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Welcome back, {currentUser?.name?.split(' ')[0] || 'Student'}! 👋</h1>
          <p>Continue your NCLEX prep journey</p>
        </div>
      </div>

      {/* Unread Messages Banner */}
      {unreadMessageCount > 0 && (
        <div className="unread-messages-banner" onClick={() => setCurrentView('messages')}>
          <div className="banner-content">
            <span className="banner-icon">📬</span>
            <span className="banner-text">
              You have <strong>{unreadMessageCount}</strong> unread message{unreadMessageCount !== 1 ? 's' : ''}
            </span>
          </div>
          <button className="banner-btn">View Messages →</button>
        </div>
      )}

      {/* ================================================================= */}
      {/* FEATURE #7: LIVE LECTURE BANNER */}
      {/* ================================================================= */}
      {liveLectureInfo && (
        <div className={`live-lecture-banner ${liveLectureInfo.status}`}>
          <div className="live-banner-content">
            <div className="live-indicator">
              {liveLectureInfo.status === 'live' ? (
                <>
                  <span className="pulse-dot"></span>
                  <span className="live-text">LIVE NOW</span>
                </>
              ) : (
                <>
                  <span className="countdown-icon">⏰</span>
                  <span className="countdown-text">{liveLectureInfo.countdown}</span>
                </>
              )}
            </div>
            <div className="live-info">
              <span className="live-title">{liveLectureInfo.title || 'Live Lecture'}</span>
              <span className="live-time">
                {formatTime(liveLectureInfo._startTime)} - {formatTime(liveLectureInfo._endTime)}
              </span>
            </div>
          </div>
          <button className="btn-join-lecture" onClick={() => setCurrentView('calendar')}>
            {liveLectureInfo.status === 'live' ? 'Join Now →' : 'View Details →'}
          </button>
        </div>
      )}

      {/* Course Banner */}
      <div className="course-banner">
        <div className="banner-content">
          <div className="banner-icon">📚</div>
          <div className="banner-info">
            <h3>{cohort.name}</h3>
            <p>{formatDate(cohortStartDate)} - {formatDate(cohortEndDate)}</p>
          </div>
        </div>
        <div className="banner-stats">
          <div className="banner-stat">
            <span className="stat-value">Week {currentWeek}</span>
            <span className="stat-label">Current</span>
          </div>
          <div className="banner-stat">
            <span className="stat-value">{modules.length}/6</span>
            <span className="stat-label">Modules Available</span>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* FEATURE #6: NEXT DUE ITEM CALLOUT */}
      {/* ================================================================= */}
      {nextDueItem && (
        <div className={`next-due-callout ${getDueUrgencyClass(nextDueItem._date)}`}>
          <div className="callout-icon">{getEventTypeIcon(nextDueItem.type || nextDueItem.itemType)}</div>
          <div className="callout-content">
            <span className="callout-label">Next Up</span>
            <span className="callout-title">{nextDueItem.title}</span>
            <span className="callout-meta">
              Due {formatDate(nextDueItem._date)}
              {nextDueItem.points && ` • ${nextDueItem.points} pts`}
            </span>
          </div>
          <button 
            className="btn-secondary small"
            onClick={() => {
              setSelectedWeek(nextDueItem.weekNumber);
              setCurrentView('module');
            }}
          >
            View →
          </button>
        </div>
      )}

      {/* ================================================================= */}
      {/* FEATURE #5: WEEKLY PROGRESS BAR */}
      {/* ================================================================= */}
      <div className="weekly-progress-card">
        <div className="progress-header">
          <h3>📊 Week {currentWeek} Progress</h3>
          <span className="progress-count">
            {weeklyProgress.completed} of {weeklyProgress.total} items completed
          </span>
        </div>
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill"
            style={{ width: `${weeklyProgress.percentage}%` }}
          ></div>
        </div>
        <div className="progress-percentage">{weeklyProgress.percentage}%</div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid compact">
        <div className="stat-card">
          <div className="stat-icon blue">📖</div>
          <div className="stat-content">
            <div className="stat-value">{modules.length}</div>
            <div className="stat-label">Available Modules</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">🎓</div>
          <div className="stat-content">
            <div className="stat-value">
              {upcomingEvents.filter(e => e.type === 'lecture' || e.type === 'live-lecture').length}
            </div>
            <div className="stat-label">Lectures This Week</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">📝</div>
          <div className="stat-content">
            <div className="stat-value">
              {upcomingEvents.filter(e => e.type !== 'lecture' && e.type !== 'live-lecture').length}
            </div>
            <div className="stat-label">Assignments Due</div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Upcoming Events Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>📅 Upcoming This Week</h2>
            <button className="link-btn" onClick={() => setCurrentView('calendar')}>
              View Calendar →
            </button>
          </div>

          <div className="events-list">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map(event => (
                <div key={event.id} className={`event-card ${event.type}`}>
                  <div className="event-icon">{getEventTypeIcon(event.type)}</div>
                  <div className="event-info">
                    <div className="event-title">{event.title}</div>
                    <div className="event-meta">
                      <span>{formatDate(event._date)}</span>
                      {event._startTime && (
                        <span> • {formatTime(event._startTime)}</span>
                      )}
                      {event.points && (
                        <span> • {event.points} pts</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state small">
                <span className="empty-icon">📭</span>
                <p>No upcoming events this week</p>
              </div>
            )}
          </div>
        </div>

        {/* ================================================================= */}
        {/* FEATURE #1 & #2: MODULES WITH PROGRESS & CURRENT WEEK HIGHLIGHT */}
        {/* ================================================================= */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>📚 Course Modules</h2>
          </div>

          <div className="modules-grid">
            {modules.map(module => {
              const weekNumber = module.weekNumber || module.week_number;
              const itemCount = (module.items || module.module_items || []).length;
              const progress = getModuleProgressData(weekNumber);
              const isCurrentWeek = weekNumber === currentWeek;
              const assignmentStatus = getAssignmentStatus(module);
              
              return (
                <div
                  key={module.id}
                  className={`module-card ${isCurrentWeek ? 'current' : ''} ${progress.percentage === 100 ? 'completed' : ''}`}
                  onClick={() => handleModuleClick(weekNumber)}
                >
                  <div className="module-header">
                    <span className="week-badge">Week {weekNumber}</span>
                    {isCurrentWeek && (
                      <span className="current-badge">Current</span>
                    )}
                    {progress.percentage === 100 && (
                      <span className="completed-badge">✓</span>
                    )}
                  </div>
                  <h4>{module.title}</h4>
                  <div className="module-meta">
                    <span>{itemCount} items</span>
                  </div>
                  
                  {/* FEATURE #1: Progress bar on each module */}
                  <div className="module-progress">
                    <div className="module-progress-bar">
                      <div 
                        className="module-progress-fill"
                        style={{ width: `${progress.percentage}%` }}
                      ></div>
                    </div>
                    <span className="module-progress-text">
                      {progress.completed}/{progress.total}
                    </span>
                  </div>

                  {/* Assignment submission status */}
                  {assignmentStatus.hasAssignments && (
                    <div style={{
                      marginTop: '8px',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: assignmentStatus.allDone ? '#ecfdf5' : '#fef3c7',
                      color: assignmentStatus.allDone ? '#065f46' : '#92400e',
                      border: `1px solid ${assignmentStatus.allDone ? '#a7f3d0' : '#fde68a'}`
                    }}>
                      <span>{assignmentStatus.allDone ? '✅' : '⚠️'}</span>
                      <span>
                        {assignmentStatus.allDone 
                          ? 'All assignments submitted' 
                          : `${assignmentStatus.submitted}/${assignmentStatus.total} assignments submitted`
                        }
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Instructor Feedback Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>💬 Instructor Feedback</h2>
          </div>
          
          {(() => {
            const feedbackSubmissions = getSubmissionsWithFeedback ? getSubmissionsWithFeedback() : [];
            
            if (feedbackSubmissions.length === 0) {
              return (
                <div className="empty-state small">
                  <span className="empty-icon">💬</span>
                  <p>No feedback yet</p>
                </div>
              );
            }

            // Get module item details for each submission
            const getItemDetails = (moduleItemId) => {
              for (const mod of (allModules || [])) {
                const item = (mod.items || []).find(i => i.id === moduleItemId);
                if (item) {
                  return { 
                    title: item.title, 
                    type: item.type,
                    weekNumber: mod.weekNumber || mod.week_number 
                  };
                }
              }
              return { title: 'Unknown Item', type: 'assignment', weekNumber: null };
            };

            return (
              <div className="feedback-list">
                {feedbackSubmissions.slice(0, 5).map(submission => {
                  const itemDetails = getItemDetails(submission.module_item_id);
                  return (
                    <div key={submission.id} className="feedback-card">
                      <div className="feedback-header">
                        <span className="feedback-item-title">{itemDetails.title}</span>
                        {submission.grade !== null && (
                          <span className="feedback-grade">{submission.grade}/100</span>
                        )}
                      </div>
                      <div className="feedback-content">
                        <p>{submission.feedback}</p>
                      </div>
                      <div className="feedback-meta">
                        <span>Week {itemDetails.weekNumber}</span>
                        {submission.graded_at && (
                          <span> • {new Date(submission.graded_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Contact Instructor Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>📧 Contact Instructor</h2>
          </div>
          <div className="contact-instructor-card">
            <p>Have questions? Need help with course material?</p>
            <button 
              className="btn-primary"
              onClick={() => setCurrentView('messages')}
            >
              ✉️ Go to Messages
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
